import {
  Button,
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerHeaderTitle,
  Dropdown,
  Field,
  InfoLabel,
  Input,
  LabelProps,
  Option,
  Switch,
  Tag,
  TagGroup,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import React, { useState } from 'react';
import { InjectedIntlProps } from 'react-intl';
import { injectIntlAndObserver } from '../../../../utils';
import { ChartGrain } from '../../DashboardCards/ReportCard/ReportCard.types';
import { capitalizeFirstLetter } from '../../OverviewPage/utils/utils';
import { useReportDetailPageStyles } from '../DetailPage.styles';

interface ISchedulePanelProps extends InjectedIntlProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onSave: (isEnabled: boolean, emails?: string[], frequency?: ChartGrain) => void;
  defaultEnabled: boolean;
  defaultEmails?: string[];
  defaultFreq?: ChartGrain;
}
const AVAILABLE_FREQUENCIES: ChartGrain[] = [ChartGrain.DAILY, ChartGrain.WEEKLY, ChartGrain.MONTHLY, ChartGrain.YEARLY];

export const SchedulePanel = injectIntlAndObserver(function ModifyColumnsPanelComponent({
  intl,
  isOpen,
  setIsOpen,
  onSave,
  defaultEnabled,
  defaultEmails,
  defaultFreq,
}: ISchedulePanelProps) {
  const classNames = useReportDetailPageStyles();

  const [isEnabled, setIsEnabled] = useState(defaultEnabled);
  const [selectedFreq, setSelectedFreq] = useState(defaultFreq ?? ChartGrain.MONTHLY);
  const [selectedEmails, setSelectedEmails] = useState<string[]>(defaultEmails ?? []);
  const [emailInput, setEmailInput] = useState('');
  const [emailError, setEmailError] = useState('');

  const handleEmailInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      addEmail();
    }
  };

  const handleEmailInputChange = (e: React.ChangeEvent<HTMLInputElement>, data: { value: string }) => {
    setEmailInput(data.value);

    if (emailError) {
      setEmailError('');
    }
  };

  const addEmail = () => {
    const email = emailInput.trim();

    setEmailError('');

    if (!email) {
      setEmailError('Email cannot be empty');
      return;
    }

    if (!isValidEmail(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    if (selectedEmails.includes(email)) {
      setEmailError('This email is already added');
      return;
    }

    setSelectedEmails([...selectedEmails, email]);
    setEmailInput('');
  };

  const handleOnEnableChange = (enabled: boolean) => {
    if (!enabled) {
      setEmailInput('');
      setEmailError('');
      setSelectedEmails([]);
    }
    setIsEnabled(enabled);
  };

  const handleOnSave = () => {
    setIsOpen(false);
    onSave(isEnabled, selectedEmails, selectedFreq);
  };

  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  return (
    <Drawer type="overlay" position="end" size="small" open={isOpen} onOpenChange={(_, { open }) => setIsOpen(open)}>
      <DrawerHeader>
        <DrawerHeaderTitle
          action={<Button aria-label="close" onClick={() => setIsOpen(false)} appearance="subtle" icon={<Dismiss24Regular />} />}
        >
          Schedule
        </DrawerHeaderTitle>
      </DrawerHeader>
      <DrawerBody>
        <Switch label="Enable" checked={isEnabled} onChange={(e, checked) => handleOnEnableChange(checked.checked.valueOf())} />
        <div className={classNames.schedulePanelBody}>
          <Field
            required
            label={{
              children: (_: unknown, slotProps: LabelProps) => (
                <InfoLabel {...slotProps} info="How often the report is sent">
                  Frequency
                </InfoLabel>
              ),
            }}
          >
            <Dropdown
              className={classNames.schedulePanelItem}
              disabled={!isEnabled}
              placeholder={capitalizeFirstLetter(
                Object.keys(ChartGrain).filter((key) => ChartGrain[key as keyof typeof ChartGrain] === selectedFreq)[0]
              )}
              selectedOptions={[selectedFreq]}
              onOptionSelect={(event, data) => setSelectedFreq(data.optionValue as ChartGrain)}
            >
              {AVAILABLE_FREQUENCIES.map((freq) => (
                <Option key={freq} value={freq}>
                  {capitalizeFirstLetter(Object.keys(ChartGrain).filter((key) => ChartGrain[key as keyof typeof ChartGrain] === freq)[0])}
                </Option>
              ))}
            </Dropdown>
          </Field>

          <Field
            required
            validationState={emailError ? 'error' : undefined}
            validationMessage={emailError}
            label={{
              children: (_: unknown, slotProps: LabelProps) => (
                <InfoLabel {...slotProps} info="Emails to be sent the report">
                  Email
                </InfoLabel>
              ),
            }}
          >
            <Input
              disabled={!isEnabled}
              type="email"
              placeholder="Enter email address"
              value={emailInput}
              onChange={handleEmailInputChange}
              onKeyDown={handleEmailInputKeyDown}
              onBlurCapture={addEmail}
            />
          </Field>
          <TagGroup
            onDismiss={(_, { value }) => setSelectedEmails(selectedEmails.filter((email) => email !== value))}
            className={classNames.schedulePanelItem}
          >
            {selectedEmails.map((email) => (
              <Tag key={email} dismissible dismissIcon={{ 'aria-label': 'remove' }} value={email}>
                {email}
              </Tag>
            ))}
          </TagGroup>
        </div>
      </DrawerBody>
      <DrawerFooter>
        <Button onClick={handleOnSave} appearance="primary" disabled={isEnabled && !selectedEmails.length}>
          Save
        </Button>
        <Button onClick={() => setIsOpen(false)} appearance="secondary">
          Cancel
        </Button>
      </DrawerFooter>
    </Drawer>
  );
});
